import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../state/session.tsx';
import { companyHasPlanFeature } from '../plan-gate.tsx';
import { classes } from '../ui.tsx';

/**
 * The two views under the one "Quotes" entry in the navigation: the quotes
 * themselves, and the Magic Quote walkthroughs some of them were built from.
 *
 * A tab strip rather than a second top-level menu item, which is what this
 * replaces. A walkthrough is not a different kind of work from a quote — it is
 * one of the three ways a quote gets started (components/quote/new-quote
 * methods) — and two entries in the main bar said otherwise to everyone who
 * looked at it. The walkthroughs are still one click away for the cleaner who
 * comes back to find one; they are just one click from *here*.
 *
 * Hidden entirely when there is nothing to switch between: a company whose
 * plan does not carry Magic Quote has no walkthroughs to list, and a strip
 * with one tab is a heading wearing a costume. The pages themselves still
 * answer to their URLs regardless (a saved link to `/quotes/walkthroughs`
 * keeps working and explains itself), on the same principle as the plan-gated
 * navigation items in app-layout.
 */
export function QuotesTabs() {
  const { t } = useTranslation();
  const { company, can } = useSession();

  const showWalkthroughs = can('quotes:read') && companyHasPlanFeature(company, 'magic_quote');
  if (!showWalkthroughs) return null;
  const tabs = [
    { to: '/quotes', label: t('quotes.tabs.quotes') },
    { to: '/quotes/walkthroughs', label: t('quotes.tabs.walkthroughs') },
  ];

  return (
    <nav aria-label={t('quotes.tabs.ariaLabel')} className="mb-6 border-b border-[color:var(--color-line)]">
      <ul className="-mb-px flex flex-wrap gap-1">
        {tabs.map((tab) => (
          <li key={tab.to}>
            <NavLink
              to={tab.to}
              // `/quotes` would otherwise stay lit on `/quotes/walkthroughs`,
              // because every path under it matches the prefix.
              end
              className={({ isActive }) =>
                classes(
                  'inline-block border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'border-[color:var(--color-brand)] text-[color:var(--color-brand-strong)]'
                    : 'border-transparent text-[color:var(--color-muted)] hover:border-[color:var(--color-line)] hover:text-[color:var(--color-ink)]',
                )
              }
            >
              {tab.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
